import { useEffect, useRef, useCallback } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

interface UseSidebarTourOptions {
  enabled: boolean;
  user: any;
  hasActivePlan: boolean;
  sidebarState?: 'expanded' | 'collapsed';
  setSidebarOpen?: (open: boolean) => void;
}

// Tour step definitions with descriptions
const tourSteps = [
  {
    selector: 'sidebar-trigger',
    title: 'Sidebar Toggle',
    description: 'Click here to expand or collapse the sidebar. This helps you navigate between different sections of the application.',
    side: 'bottom' as const,
    align: 'start' as const,
  },
  {
    selector: 'overview',
    title: 'Overview',
    description: 'View your dashboard and analytics here. Get insights into your messaging activity and performance metrics.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'inbox',
    title: 'Inbox',
    description: 'Manage all your conversations in one place. Reply to messages, track customer interactions, and stay on top of your communications.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'contacts',
    title: 'Contacts',
    description: 'Access and manage your contact list. Organize your customers, add new contacts, and view contact details.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'templates',
    title: 'Templates',
    description: 'Create and manage message templates. Save time by using pre-written messages for common scenarios.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'campaigns',
    title: 'Campaigns',
    description: 'Create and manage marketing campaigns. Send bulk messages, schedule campaigns, and track their performance.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'whatsapp-flows',
    title: 'WhatsApp Forms',
    description: 'Build interactive WhatsApp Forms for customer engagement. Create forms, surveys, and interactive experiences.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'automation',
    title: 'Automation',
    description: 'Set up automated workflows and triggers. Automate your messaging and business processes.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'notifications',
    title: 'Notifications',
    description: 'View and manage your notifications. Stay updated with important alerts and messages.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-business-accounts',
    title: 'Business Accounts',
    description: 'Manage your business accounts. Connect and configure your WhatsApp Business accounts.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-analytics',
    title: 'Analytics',
    description: 'View detailed analytics and reports. Track your messaging performance, engagement metrics, and business insights.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-commerce',
    title: 'Commerce',
    description: 'Manage your e-commerce integration. Connect your store and enable shopping features.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-integrations',
    title: 'Integrations',
    description: 'Connect with third-party services. Integrate your favorite tools and platforms.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-team-management',
    title: 'Team Management',
    description: 'Manage your team members and their permissions. Add team members, assign roles, and control access.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'settings-settings',
    title: 'Settings',
    description: 'Configure your account settings, preferences, and manage your profile. Customize your experience here.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-account-selector',
    title: 'Business Account Selector',
    description: 'Switch between different business accounts. Click here to view and manage your connected business accounts.',
    side: 'bottom' as const,
    align: 'start' as const,
  },
];

const TOUR_COMPLETED_KEY = 'sidebar-tour-shown';
const TOUR_CURRENT_STEP_KEY = 'sidebar-tour-current-step';

export function useSidebarTour({ enabled, user, hasActivePlan, sidebarState, setSidebarOpen }: UseSidebarTourOptions) {
  const driverObjRef = useRef<any>(null);
  const hasShownTourRef = useRef(false);
  const clickOutsideHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const keyboardHandlerRef = useRef<((e: KeyboardEvent) => void) | null>(null);
  const isNavigatingRef = useRef(false);

  const saveCurrentStep = useCallback((stepIndex: number) => {
    localStorage.setItem(TOUR_CURRENT_STEP_KEY, stepIndex.toString());
  }, []);

  const getCurrentStep = useCallback(() => {
    const saved = localStorage.getItem(TOUR_CURRENT_STEP_KEY);
    return saved ? parseInt(saved, 10) : 0;
  }, []);

  const clearCurrentStep = useCallback(() => {
    localStorage.removeItem(TOUR_CURRENT_STEP_KEY);
  }, []);

  const stopTour = useCallback((saveStep: boolean = true) => {
    if (driverObjRef.current) {
      // Get current step before destroying if we need to save it
      if (saveStep) {
        try {
          // Try to get the active step from the driver instance
          const activeElement = document.querySelector('.driver-highlighted-element');
          if (activeElement) {
            // Find which step this element corresponds to
            const allSteps = tourSteps.map(stepDef => 
              document.querySelector(`[data-tour="${stepDef.selector}"]`)
            );
            const stepIndex = allSteps.findIndex(el => el === activeElement);
            if (stepIndex >= 0) {
              saveCurrentStep(stepIndex);
            }
          }
        } catch (e) {
          // If we can't get the step, don't save
        }
      }
      
      driverObjRef.current.destroy();
      driverObjRef.current = null;
    }
    
    // Reset navigation flag
    isNavigatingRef.current = false;
    
    // Remove click outside handler
    if (clickOutsideHandlerRef.current) {
      document.removeEventListener('click', clickOutsideHandlerRef.current);
      clickOutsideHandlerRef.current = null;
    }

    // Remove keyboard handler
    if (keyboardHandlerRef.current) {
      document.removeEventListener('keydown', keyboardHandlerRef.current);
      keyboardHandlerRef.current = null;
    }
  }, [saveCurrentStep]);

  const startTour = useCallback((startFromStep?: number) => {
    // Ensure sidebar is expanded before starting tour
    if (sidebarState === 'collapsed' && setSidebarOpen) {
      setSidebarOpen(true);
      // Wait for sidebar to expand
      setTimeout(() => {
        initializeAndStartTour(startFromStep);
      }, 500);
    } else {
      initializeAndStartTour(startFromStep);
    }
  }, [sidebarState, setSidebarOpen]);

  function initializeAndStartTour(startFromStep?: number) {
    // Build steps dynamically based on what elements exist
    const allAvailableSteps = tourSteps
      .map(stepDef => {
        const element = document.querySelector(`[data-tour="${stepDef.selector}"]`);
        if (!element) return null;
        return { stepDef, element };
      })
      .filter(step => step !== null);

    // Only start tour if we have at least one step
    if (allAvailableSteps.length === 0) {
      return;
    }

    // Determine starting step
    const stepToStart = startFromStep !== undefined 
      ? startFromStep 
      : getCurrentStep();
    
    // Find which available step corresponds to the start index
    let validStartIndex = 0;
    let currentTourIndex = 0;
    for (let i = 0; i < tourSteps.length; i++) {
      const element = document.querySelector(`[data-tour="${tourSteps[i].selector}"]`);
      if (element) {
        if (currentTourIndex === stepToStart || i === stepToStart) {
          validStartIndex = currentTourIndex;
          break;
        }
        currentTourIndex++;
      }
    }
    
    const validStartStep = Math.min(Math.max(0, validStartIndex), allAvailableSteps.length - 1);
    
    // Slice the steps array to start from the correct position
    const stepsToShow = allAvailableSteps.slice(validStartStep);
    
    // Build the steps array with proper button configuration
    const steps = stepsToShow.map((stepData, index) => {
      const isLastStep = index === stepsToShow.length - 1;
      const stepDef = stepData!.stepDef;
      
      return {
        element: stepData!.element,
        popover: {
          title: stepDef.title,
          description: stepDef.description,
          side: stepDef.side,
          align: stepDef.align,
          closeBtnText: 'Done',
          nextBtnText: 'Next',
          prevBtnText: 'Previous',
          // On last step, show only Previous and Done (hide Next)
          showButtons: isLastStep ? ['previous', 'close'] : ['previous', 'next', 'close'],
        },
      };
    });

    // Destroy existing tour if any
    if (driverObjRef.current) {
      driverObjRef.current.destroy();
    }

    // Remove any existing click handlers
    if (clickOutsideHandlerRef.current) {
      document.removeEventListener('click', clickOutsideHandlerRef.current);
      clickOutsideHandlerRef.current = null;
    }

    // Remove any existing keyboard handlers
    if (keyboardHandlerRef.current) {
      document.removeEventListener('keydown', keyboardHandlerRef.current);
      keyboardHandlerRef.current = null;
    }

    // Inject custom theme styles for driver.js popover
    const styleId = 'driver-theme-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    
    // Update styles to match theme
    styleElement.textContent = `
      .driver-popover {
        background: hsl(var(--popover)) !important;
        border: 1px solid hsl(var(--border)) !important;
        border-radius: 12px !important;
        box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
        padding: 1.5rem !important;
        font-family: inherit !important;
        min-width: 300px !important;
        max-width: 400px !important;
      }
      .driver-popover-title {
        color: hsl(var(--foreground)) !important;
        font-weight: 600 !important;
        font-size: 1.125rem !important;
        margin-bottom: 0.5rem !important;
        line-height: 1.4 !important;
      }
      .driver-popover-description {
        color: hsl(var(--muted-foreground)) !important;
        font-size: 0.875rem !important;
        line-height: 1.5 !important;
        margin-bottom: 1.25rem !important;
      }
      .driver-popover-footer {
        display: flex !important;
        gap: 0.5rem !important;
        justify-content: flex-end !important;
        margin-top: 1rem !important;
        padding-top: 1rem !important;
        border-top: 1px solid hsl(var(--border)) !important;
      }
      .driver-popover-footer button {
        padding: 0.5rem 1rem !important;
        border-radius: 8px !important;
        font-size: 0.875rem !important;
        font-weight: 500 !important;
        transition: all 0.2s ease !important;
        cursor: pointer !important;
        border: none !important;
        outline: none !important;
      }
      .driver-popover-footer .driver-prev-btn {
        background: hsl(var(--secondary)) !important;
        color: hsl(var(--secondary-foreground)) !important;
      }
      .driver-popover-footer .driver-prev-btn:hover {
        background: hsl(var(--secondary-hover)) !important;
      }
      .driver-popover-footer .driver-next-btn {
        background: linear-gradient(104.53deg, #08090A 0%, #29292D 66.09%) !important;
        color: white !important;
      }
      .driver-popover-footer .driver-next-btn:hover {
        opacity: 0.9 !important;
        transform: translateY(-1px) !important;
      }
      .driver-popover-footer .driver-close-btn {
        background: linear-gradient(104.53deg, #08090A 0%, #29292D 66.09%) !important;
        color: white !important;
      }
      .driver-popover-footer .driver-close-btn:hover {
        opacity: 0.9 !important;
        transform: translateY(-1px) !important;
      }
      .driver-popover-progress {
        background: hsl(var(--muted)) !important;
        height: 4px !important;
        border-radius: 2px !important;
        margin-bottom: 1rem !important;
      }
      .driver-popover-progress-item {
        background: linear-gradient(104.53deg, #08090A 0%, #29292D 66.09%) !important;
        height: 100% !important;
        border-radius: 2px !important;
        transition: width 0.3s ease !important;
      }
     
      .driver-highlighted-element {
        border-radius: 8px !important;
        box-shadow: 0 0 0 4px rgba(8, 9, 10, 0.1) !important;
      }
    `;

    // Initialize driver.js
    const driverObj = driver({
      showProgress: true,
      steps: steps as any,
      allowClose: true,
      onDestroyStarted: () => {
        // This is called when user clicks "Done" or close button
        // Mark tour as completed
        localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
        clearCurrentStep();
        hasShownTourRef.current = true;
        // Clean up click handler
        if (clickOutsideHandlerRef.current) {
          document.removeEventListener('click', clickOutsideHandlerRef.current);
          clickOutsideHandlerRef.current = null;
        }

        // Clean up keyboard handler
        if (keyboardHandlerRef.current) {
          document.removeEventListener('keydown', keyboardHandlerRef.current);
          keyboardHandlerRef.current = null;
        }

        // Force destroy
        if (driverObjRef.current) {
          driverObjRef.current.destroy();
          driverObjRef.current = null;
        }
      },
      onDestroyed: () => {
        // Clean up after tour is destroyed
        driverObjRef.current = null;
        isNavigatingRef.current = false;
        
        // Remove click outside handler
        if (clickOutsideHandlerRef.current) {
          document.removeEventListener('click', clickOutsideHandlerRef.current);
          clickOutsideHandlerRef.current = null;
        }

        // Remove keyboard handler
        if (keyboardHandlerRef.current) {
          document.removeEventListener('keydown', keyboardHandlerRef.current);
          keyboardHandlerRef.current = null;
        }
      },
      onHighlightStarted: (step: any) => {
        // Save current step when it's highlighted
        // Find the step in the original tourSteps array
        const stepIndex = tourSteps.findIndex(stepDef => {
          const element = document.querySelector(`[data-tour="${stepDef.selector}"]`);
          return element === step.element;
        });
        if (stepIndex >= 0) {
          saveCurrentStep(stepIndex);
        }
      },
    });

    driverObjRef.current = driverObj;

    // Add keyboard navigation handler
    keyboardHandlerRef.current = (e: KeyboardEvent) => {
      // Prevent multiple rapid key presses
      if (isNavigatingRef.current) {
        return;
      }

      // Only handle if tour is active and not typing in an input
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement instanceof HTMLElement && activeElement.isContentEditable)
      );

      if (isInputFocused) {
        return; // Don't interfere with typing
      }

      // Enter key - move to next step (one step at a time)
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        
        // Check if tour is still active
        if (!driverObjRef.current) {
          return;
        }

        // Set navigating flag to prevent multiple rapid presses
        isNavigatingRef.current = true;

        try {
          // Move to next step (one step only)
          driverObjRef.current.moveNext();
          
          // Reset flag after a short delay to allow step transition
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 300);
        } catch (err) {
          isNavigatingRef.current = false;
          // Ignore errors
        }
      }

      // Backspace key - move to previous step (one step at a time)
      if (e.key === 'Backspace' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        
        // Check if tour is still active
        if (!driverObjRef.current) {
          return;
        }

        // Set navigating flag to prevent multiple rapid presses
        isNavigatingRef.current = true;

        try {
          // Move to previous step (one step only)
          driverObjRef.current.movePrevious();
          
          // Reset flag after a short delay to allow step transition
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 300);
        } catch (err) {
          isNavigatingRef.current = false;
          // Ignore errors
        }
      }
    };

    // Add keyboard event listener
    document.addEventListener('keydown', keyboardHandlerRef.current, true);

    // Add click outside handler to stop tour
    clickOutsideHandlerRef.current = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Check if click is on the overlay (backdrop) or outside the popover
      const isOverlay = target.classList.contains('driver-overlay') || 
                       target.classList.contains('driver-overlay-animated');
      const isPopover = target.closest('.driver-popover');
      const isHighlighted = target.closest('.driver-highlighted-element');
      const isPopoverButton = target.closest('.driver-popover button');
      
      // If clicking on overlay (but not on popover, highlighted element, or popover buttons), close tour
      if (isOverlay && !isPopover && !isHighlighted && !isPopoverButton) {
        e.preventDefault();
        e.stopPropagation();
        // Close the tour immediately
        if (driverObjRef.current) {
          localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
          clearCurrentStep();
          hasShownTourRef.current = true;
          try {
            driverObjRef.current.destroy();
          } catch (err) {
            // Ignore errors
          }
          driverObjRef.current = null;
        }
        // Remove click handler
        if (clickOutsideHandlerRef.current) {
          document.removeEventListener('click', clickOutsideHandlerRef.current);
          clickOutsideHandlerRef.current = null;
        }

        // Remove keyboard handler
        if (keyboardHandlerRef.current) {
          document.removeEventListener('keydown', keyboardHandlerRef.current);
          keyboardHandlerRef.current = null;
        }

        // Also remove overlay manually if it still exists
        const overlay = document.querySelector('.driver-overlay');
        if (overlay) {
          overlay.remove();
        }
        const popover = document.querySelector('.driver-popover');
        if (popover) {
          popover.remove();
        }
      }
    };

    // Add event listener for click outside (use capture phase to catch early)
    setTimeout(() => {
      if (clickOutsideHandlerRef.current) {
        document.addEventListener('click', clickOutsideHandlerRef.current, true);
      }
    }, 100);
    
    // Also add mousedown listener for better detection
    const mouseDownHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isOverlay = target.classList.contains('driver-overlay') || 
                       target.classList.contains('driver-overlay-animated');
      const isPopover = target.closest('.driver-popover');
      const isHighlighted = target.closest('.driver-highlighted-element');
      
      if (isOverlay && !isPopover && !isHighlighted) {
        if (driverObjRef.current) {
          localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
          clearCurrentStep();
          hasShownTourRef.current = true;
          try {
            driverObjRef.current.destroy();
          } catch (err) {
            // Ignore errors
          }
          driverObjRef.current = null;
        }
        // Remove overlays
        const overlay = document.querySelector('.driver-overlay');
        if (overlay) overlay.remove();
        const popover = document.querySelector('.driver-popover');
        if (popover) popover.remove();
        document.removeEventListener('mousedown', mouseDownHandler, true);
      }
    };
    
    setTimeout(() => {
      document.addEventListener('mousedown', mouseDownHandler, true);
    }, 100);

    // Start the tour
    driverObj.drive();

    // Watch for Done button and attach listener
    const observer = new MutationObserver(() => {
      const popover = document.querySelector('.driver-popover');
      if (popover) {
        const doneButton = Array.from(popover.querySelectorAll('button')).find(
          btn => btn.textContent?.trim().toLowerCase() === 'done' || btn.classList.contains('driver-close-btn')
        );
        if (doneButton) {
          const handleDone = () => {
            if (driverObjRef.current) {
              localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
              clearCurrentStep();
              hasShownTourRef.current = true;
              if (clickOutsideHandlerRef.current) {
                document.removeEventListener('click', clickOutsideHandlerRef.current);
                clickOutsideHandlerRef.current = null;
              }
              if (keyboardHandlerRef.current) {
                document.removeEventListener('keydown', keyboardHandlerRef.current);
                keyboardHandlerRef.current = null;
              }
              try {
                driverObjRef.current.destroy();
              } catch (e) {
                // Ignore
              }
              driverObjRef.current = null;
              // Remove overlays
              const overlay = document.querySelector('.driver-overlay');
              if (overlay) overlay.remove();
              const popoverEl = document.querySelector('.driver-popover');
              if (popoverEl) popoverEl.remove();
            }
            observer.disconnect();
          };
          doneButton.addEventListener('click', handleDone, { once: true });
        }
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    // Cleanup observer when tour is destroyed
    const originalDestroy = driverObjRef.current?.destroy;
    if (originalDestroy) {
      driverObjRef.current.destroy = function() {
        observer.disconnect();
        return originalDestroy.call(this);
      };
    }

    // Mark as shown immediately to prevent multiple tours
    hasShownTourRef.current = true;
  }

  // Auto-start tour disabled - tour will only start when help icon is clicked
  // useEffect(() => {
  //   // Only run automatically if enabled, user is logged in, has active plan, and tour hasn't been shown
  //   if (!enabled || !user || !hasActivePlan || hasShownTourRef.current) {
  //     return;
  //   }

  //   // Check if tour was already completed (stored in localStorage)
  //   const tourCompleted = localStorage.getItem(TOUR_COMPLETED_KEY);
  //   if (tourCompleted === 'true') {
  //     hasShownTourRef.current = true;
  //     return;
  //   }

  //   // Wait a bit for the sidebar to render before auto-starting
  //   const timer = setTimeout(() => {
  //     const savedStep = getCurrentStep();
  //     startTour(savedStep > 0 ? savedStep : undefined);
  //   }, 1500); // Wait 1.5 seconds for sidebar to render

  //   return () => {
  //     clearTimeout(timer);
  //     if (driverObjRef.current) {
  //       driverObjRef.current.destroy();
  //     }
  //     if (clickOutsideHandlerRef.current) {
  //       document.removeEventListener('click', clickOutsideHandlerRef.current);
  //     }
  //     if (keyboardHandlerRef.current) {
  //       document.removeEventListener('keydown', keyboardHandlerRef.current);
  //     }
  //   };
  // }, [enabled, user, hasActivePlan, startTour, getCurrentStep]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverObjRef.current) {
        driverObjRef.current.destroy();
      }
      if (clickOutsideHandlerRef.current) {
        document.removeEventListener('click', clickOutsideHandlerRef.current);
      }
      if (keyboardHandlerRef.current) {
        document.removeEventListener('keydown', keyboardHandlerRef.current);
      }
    };
  }, []);

  return {
    startTour: () => {
      const savedStep = getCurrentStep();
      startTour(savedStep > 0 ? savedStep : undefined);
    },
    stopTour,
    resetTour: () => {
      localStorage.removeItem(TOUR_COMPLETED_KEY);
      localStorage.removeItem(TOUR_CURRENT_STEP_KEY);
      hasShownTourRef.current = false;
      if (driverObjRef.current) {
        driverObjRef.current.destroy();
        driverObjRef.current = null;
      }
    },
  };
}
